import HBText from '@honeybook/hbui/dist/components/HBText/HBText';
import {
    mediumUp as desktop,
    upToMedium
} from '@honeybook/hbui/dist/styles/breakpoints';
import HBButton from '@honeybook/hbui/dist/components/HBButton/HBButton';
import colors from '@honeybook/hbui/dist/styles/colors';
import { printCss, truncatedText } from '@honeybook/hbui/dist/styles/mixins';
import { blockBoxShadow } from '@honeybook/hbui/dist/styles/variables';
import spacing from '@honeybook/hbui/dist/styles/spacing';
import { observer } from 'mobx-react';
import styled, { css } from 'styled-components';
import is, { isNot } from 'styled-is';
import {
    IInvoiceColumnHeaderBlockStyles,
    InvoiceAppearanceTypes
} from '~/types/block-styles.types';
import HBTextArea from '@honeybook/hbui/dist/components/HBTextArea/HBTextArea';
import HBClickable from '@honeybook/hbui/dist/components/HBClickable/HBClickable';
import Plus16 from '@honeybook/icons/dist/icons/Plus16';
import ArrowDown16 from '@honeybook/icons/dist/icons/ArrowDown16';

export const InvoiceContainer = styled.div<{
    forceMobile?: boolean;
    isDisabled?: boolean;
    isMobileApp?: boolean;
}>`
    border-radius: ${spacing[8]};
    box-shadow: ${blockBoxShadow};
    background-color: ${colors.white};
    margin: 0 ${spacing[4]};

    ${desktop} {
        ${isNot('forceMobile')`
            margin: initial;
        `}
    }

    ${is('isDisabled')`
        pointer-events: none;
    `}

    ${is('isMobileApp')`
        margin-top: 10px;
    `}

    ${printCss(`box-shadow: unset`)};
`;

const mobileButtonBoxShadow =
    '0px 0px 0px 1px rgba(0, 0, 0, 0.1), 0px 2px 8px rgba(0, 0, 0, 0.08);';

export const ReorderItemsDataIdValue = 'reorder-items-button';

export const InvoicePaymentScheduleContainer = styled(InvoiceContainer)<{
    forceMobile?: boolean;
    $shouldHideWhenEmpty?: boolean;
}>`
    break-inside: avoid;
    margin-top: ${spacing[24]};
    box-shadow: ${blockBoxShadow};

    ${is('$shouldHideWhenEmpty')`
        &:empty {
            display: none;
        }
    `}

    ${desktop} {
        ${isNot('forceMobile')`
            margin-top: ${spacing[40]};
        `}
    }

    ${printCss(`
        box-shadow: unset;
        margin-top: ${spacing[40]};
    `)}
`;

export const InvoiceHeaderContainer = styled.div<{
    forceMobile?: boolean;
    shouldExpandTop?: boolean;
    shouldAddExtraTopPadding?: boolean;
    isSimplifyInvoiceFeatureActive?: boolean;
}>`
    padding: ${spacing[20]} ${spacing[16]} ${spacing[24]};
    ${is('shouldExpandTop')`
        padding-top: ${props =>
            props.shouldAddExtraTopPadding ? '90px' : '74px'};
    `}

    ${desktop} {
        ${isNot('forceMobile')`
            padding: ${props =>
                props.isSimplifyInvoiceFeatureActive
                    ? '32px 20px 40px 32px'
                    : '32px 32px 40px'};
        `}
    }
`;

export const HeaderContent = styled.div<{
    forceMobile?: boolean;
    addPaddingOnTop?: boolean;
}>`
    display: flex;
    justify-content: space-between;
    row-gap: ${spacing[16]};
    margin-top: ${spacing[20]};
    ${is('addPaddingOnTop')`
        padding-top: 24px;
    `}

    ${desktop} {
        ${isNot('forceMobile')`
            row-gap: ${spacing[20]};
        `}
    }
`;

export const BillToContainer = styled.div`
    flex-grow: 1;
    max-width: 434px;
`;

export const SimplifyBillToContainer = styled.div`
    display: flex;
    flex-direction: column;
`;

export const StyledPlus = styled(Plus16)`
    margin-right: 6px;
    display: flex;
`;

export const StyledArrowDown = styled(ArrowDown16)`
    margin-left: 4px;
    display: flex;
`;

export const StyledText = styled(HBText)``;

export const DropdownContainer = styled.div`
    height: 30px;
`;

export const ParticipantContainer = styled(HBClickable)<{
    $isVisible: boolean;
}>`
    display: flex;
    align-items: center;
    cursor: pointer;
    margin-top: 8px;
    opacity: ${props => (props.$isVisible ? 1 : 0)};

    &:hover,
    &:focus-visible {
        ${StyledText} {
            color: ${colors.blue25};
        }
        ${StyledPlus} {
            svg * {
                fill: ${colors.blue25};
            }
        }
    }

    &:focus-visible {
        opacity: 1;
    }
`;

const getInputBackgroundColor = (
    isRootFocused = false,
    isInMobileApp = false
) => {
    const mobileBackground = isRootFocused ? colors.neutral100 : 'transparent';
    return css`
        background: ${isInMobileApp ? mobileBackground : 'transparent'};
        ${desktop} {
            &:hover {
                background: ${colors.neutral100};
            }
        }
        &:focus {
            cursor: auto;
            background: ${colors.neutral200};
        }
        ${upToMedium} {
            && {
                pointer-events: ${isRootFocused ? 'auto' : 'none'};
            }
        }
    `;
};

export const headerInputsStyles = ({
    isRootFocused,
    isInMobileApp
}: {
    isRootFocused: boolean;
    isInMobileApp: boolean;
}) => {
    return css`
        font-family: ProximaNova;
        margin: 2px 0 0 -4px;
        font-size: 14px;
        ${getInputBackgroundColor(isRootFocused, isInMobileApp)};
        padding: 0;
        cursor: pointer;
        border-radius: 2px;

        ::placeholder {
            font-size: 14px;
            background: ${colors.neutral100};
        }

        &:focus {
            cursor: auto;
        }
        ${desktop} {
            border: unset;
        }
    `;
};

export const HeaderText = styled(HBText).attrs({
    color: 'neutral700',
    type: 'body14'
})<{ padding?: string }>`
    padding: ${props => props.padding || null};
`;

export const PlaceholderTextArea = styled(HBTextArea)<{
    $isRootFocused: boolean;
    $isInMobileApp: boolean;
}>`
    && {
        ${props =>
            headerInputsStyles({
                isRootFocused: props.$isRootFocused,
                isInMobileApp: props.$isInMobileApp
            })};
        ${desktop} {
            min-width: 434px;
        }
    }
`;

export const BillToTextArea = styled(HBTextArea)<{ $isDropdownOpen: boolean }>`
    && {
        margin-left: -5px;
        ${desktop} {
            // overflow: hidden;
            width: 350px;
            font-size: 14px;
            background: transparent;
            padding: 4px;
            min-height: initial;

            &:hover:not(:focus) {
                background-color: ${colors.neutral100};
            }

            &:focus {
                background-color: ${colors.neutral200};
            }

            ${is('$isDropdownOpen')`
                background-color: ${colors.neutral200};
            `}
        }
    }
`;

export const InvoiceDetails = styled.div<{
    $showBillTo: boolean;
    forceMobile?: boolean;
    alignToRight?: boolean;
}>`
    flex-grow: 1;
    display: grid;
    grid-template-columns: auto;
    grid-gap: ${spacing[8]};

    ${is('alignToRight')`
        justify-content: flex-end;
    `}

    ${isNot('$showBillTo')`
        grid-template-columns: repeat(2, 1fr);
    `}

    ${desktop}, print {
        ${isNot('forceMobile')`
            justify-content: center;
            align-content: start;
            grid-template-columns: 120px 120px;
            grid-row-gap: ${spacing[16]};
            grid-column-gap: ${spacing[24]};
            
            ${isNot('$showBillTo')`
                grid-auto-flow: column;
            `}
        `}
    }
`;

export const InvoiceColumnHeaderContainer = observer(styled.div<{
    styles: IInvoiceColumnHeaderBlockStyles;
    withSidePadding?: boolean;
    withNegativeMargin?: boolean;
    showDisplayFieldsButton?: boolean;
}>`
    padding: ${spacing[20]} 0;
    ${is('withSidePadding')`
        padding: ${props =>
            props.showDisplayFieldsButton
                ? '20px 20px 20px 32px'
                : '20px 32px'};
    `}

    ${is('withNegativeMargin')`
        margin: 0 -30px;
        padding: ${spacing[20]} 30px;
    `}
    box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.05);
    border-radius: 4px 4px 0px 0px;
    border-radius: 2px;
    background-color: ${props =>
        props.styles.invoice_appearance === InvoiceAppearanceTypes.ThemeColor
            ? props.theme.styles.accent_color
            : null};

    ${printCss(`
        box-shadow: unset;    
    `)}
`);

export const MobileReorderButton = styled(HBButton).attrs({
    size: 'sm',
    buttonTheme: 'secondary-solid-secondary',
    type: 'button',
    testId: `${ReorderItemsDataIdValue}`
})`
    margin-right: 12px;
    width: 140px;
    box-shadow: ${mobileButtonBoxShadow};
    border-radius: 4px;
    height: 40px;
    left: 0;
`;

export const AppearanceActionsWrapper = styled.div`
    display: none;
    position: absolute;
    align-items: center;
    justify-content: space-between;
    padding: 12px;
    width: 100%;
    top: 0;
    > :only-child {
        margin-left: auto;
    }
`;

export const MobileServiceItemContainer = styled.div`
    margin-bottom: 20px;
    display: grid;
    gap: 12px;
`;
export const DataContainer = styled.div`
    font-size: 14px;
`;

export const HeaderColumnContainer = observer(styled.div<{
    styles: IInvoiceColumnHeaderBlockStyles;
    showDisplayFieldsButton: boolean;
}>`
    display: grid;
    grid-gap: 2px;
    grid-template-columns: ${props => `
        1fr
        ${props.styles.show_quantity_and_price ? '50px' : 'auto'}
        ${props.styles.show_unit ? '65px' : 'auto'}
        ${props.styles.show_quantity_and_price ? '75px' : 'auto'}
        ${props.styles.show_tax_1 ? '50px' : 'auto'}
        ${props.styles.show_tax_2 ? '50px' : 'auto'}
        ${props.styles.show_tax_3 ? '50px' : 'auto'}
        90px
        ${props.showDisplayFieldsButton ? '32px' : 'auto'}
        `};
    align-items: center;
    background-color: ${props =>
        props.styles.invoice_appearance === InvoiceAppearanceTypes.ThemeColor
            ? props.theme.styles.accent_color
            : null};
`);

export const ServicesContainer = styled.div<{
    forceMobile?: boolean;
    isMobile?: boolean;
    extendPadding?: boolean;
}>`
    padding: ${spacing[4]} ${spacing[16]};
    ${is('isMobile')`
        padding: 15px;
    `}
    ${desktop}, print {
        ${isNot('forceMobile')`
            padding: ${spacing[16]};
            ${is('extendPadding')`
                padding-top: ${spacing[32]};
            `}
        `}
    }
`;

export const StyledHBText = observer(styled(HBText)<{
    styles: IInvoiceColumnHeaderBlockStyles;
    truncateText?: boolean;
}>`
    text-align: center;
    color: ${props =>
        props.styles.invoice_appearance === InvoiceAppearanceTypes.ThemeColor
            ? props.theme.buttons_styles.color
            : null};
    ${is('truncateText')`
        ${truncatedText}
    `};
`);

export const Description = observer(styled(HBText)<{
    styles: IInvoiceColumnHeaderBlockStyles;
}>`
    color: ${props =>
        props.styles.invoice_appearance === InvoiceAppearanceTypes.ThemeColor
            ? props.theme.buttons_styles.color
            : null};
`);

export const RecurringInvoiceOverlay = styled.div`
    position: absolute;
    z-index: 1;
    width: 100%;
    height: 100%;
    background-color: rgba(255, 255, 255, 0.6);
    z-index: 300;
    pointer-events: none;
`;

export const InvoiceDateContainer = styled.div``;

export const ProjectDetailsContainer = styled.div`
    display: flex;
    flex-direction: column;
    margin-top: 16px;
`;

export const ProjectDetails = styled(HBText)`
    white-space: pre-line;
`;
